/**
 * Audio Processing Service
 * 
 * Manages audio playback with optional pitch shifting using SoundTouchJS AudioWorklet.
 * Switches between native audio element (fast, no pitch control) and
 * SoundTouchJS processing (slower, but allows pitch shifting).
 */

export interface AudioProcessingConfig {
    onTimeUpdate?: (currentTime: number) => void;
    onEnded?: () => void;
    onError?: (error: Error) => void;
}

type PlaybackMode = 'native' | 'soundtouch';

/**
 * Service for audio playback with pitch shifting capability
 */
export class AudioProcessingService {
    private mode: PlaybackMode = 'native';

    // Native mode
    private audioElement: HTMLAudioElement | null = null;

    // SoundTouch mode (AudioWorklet)
    private audioContext: AudioContext | null = null;
    private soundtouchNode: AudioWorkletNode | null = null;
    private sourceNode: MediaElementAudioSourceNode | null = null;

    // Shared state
    private _pitch = 0; // in semitones (-12 to +12)
    private _playbackRate = 1.0;
    private _duration = 0;
    private _volume = 1.0;

    private config: AudioProcessingConfig = {};

    /**
     * Initialize with an audio element
     */
    async init(audioElement: HTMLAudioElement, config: AudioProcessingConfig = {}): Promise<void> {
        this.audioElement = audioElement;
        this.config = config;
        this._duration = audioElement.duration || 0;

        // Setup native audio event listeners
        this._setupNativeEvents();
    }

    /**
     * Set pitch in semitones (-12 to +12)
     */
    async setPitch(semitones: number): Promise<void> {
        const oldPitch = this._pitch;
        this._pitch = Math.max(-12, Math.min(12, semitones));

        // Switch modes if needed
        if (oldPitch === 0 && this._pitch !== 0) {
            // Switch to soundtouch
            await this._switchToSoundtouch();
        } else if (oldPitch !== 0 && this._pitch === 0) {
            // Switch back to native (bypass soundtouch)
            this._bypassSoundtouch();
        } else if (this._pitch !== 0 && this.soundtouchNode) {
            // Update pitch in soundtouch mode
            this.soundtouchNode.parameters.get('pitchSemitones')!.value = this._pitch;
        }
    }

    /**
     * Set playback rate (speed)
     */
    setPlaybackRate(rate: number): void {
        this._playbackRate = Math.max(0.25, Math.min(2, rate));

        if (this.audioElement) {
            this.audioElement.playbackRate = this._playbackRate;
        }
    }

    /**
     * Set volume (0 to 1)
     */
    setVolume(volume: number): void {
        this._volume = Math.max(0, Math.min(1, volume));

        if (this.audioElement) {
            this.audioElement.volume = this._volume;
        }
    }

    /**
     * Play audio
     */
    async play(): Promise<void> {
        if (this.audioElement) {
            // Resume audio context if in soundtouch mode
            if (this.mode === 'soundtouch' && this.audioContext && this.audioContext.state === 'suspended') {
                await this.audioContext.resume();
            }
            await this.audioElement.play();
        }
    }

    /**
     * Pause audio
     */
    pause(): void {
        if (this.audioElement) {
            this.audioElement.pause();
        }
    }

    /**
     * Seek to a specific time
     */
    seekTo(time: number): void {
        if (this.audioElement) {
            this.audioElement.currentTime = Math.max(0, Math.min(this._duration, time));
        }
    }

    /**
     * Get current playback time
     */
    getCurrentTime(): number {
        return this.audioElement?.currentTime ?? 0;
    }

    /**
     * Get duration
     */
    getDuration(): number {
        return this._duration;
    }

    /**
     * Check if playing
     */
    isPlaying(): boolean {
        return this.audioElement ? !this.audioElement.paused : false;
    }

    /**
     * Get current pitch in semitones
     */
    getPitch(): number {
        return this._pitch;
    }

    /**
     * Get current mode
     */
    getMode(): PlaybackMode {
        return this.mode;
    }

    /**
     * Destroy and cleanup
     */
    destroy(): void {
        this.pause();

        if (this.mode === 'soundtouch') {
            this._cleanupSoundtouch();
        }

        this.audioElement = null;
    }

    // ─── Private Methods ─────────────────────────────────────────────────────

    private _setupNativeEvents(): void {
        if (!this.audioElement) return;

        this.audioElement.addEventListener('timeupdate', () => {
            this.config.onTimeUpdate?.(this.audioElement!.currentTime);
        });

        this.audioElement.addEventListener('ended', () => {
            this.config.onEnded?.();
        });

        this.audioElement.addEventListener('loadedmetadata', () => {
            this._duration = this.audioElement!.duration;
        });
    }

    private async _switchToSoundtouch(): Promise<void> {
        if (!this.audioElement) return;

        // Initialize audio context if needed
        if (!this.audioContext) {
            this.audioContext = new AudioContext();
        }

        // Create source node from audio element (only once!)
        if (!this.sourceNode) {
            this.sourceNode = this.audioContext.createMediaElementSource(this.audioElement);
        }

        // Load the AudioWorklet module if not already loaded
        if (!this.soundtouchNode) {
            try {
                // Use inline worklet URL - works in both dev and as library
                const workletUrl = new URL('./soundtouch-worklet.js', import.meta.url).href;
                await this.audioContext.audioWorklet.addModule(workletUrl);
            } catch (error) {
                console.error('[AudioProcessing] Failed to load soundtouch worklet:', error);
                return;
            }

            // Create the SoundTouch AudioWorkletNode
            this.soundtouchNode = new AudioWorkletNode(this.audioContext, 'soundtouch-processor');
        }

        // Connect: sourceNode -> soundtouchNode -> destination
        this.sourceNode.disconnect();
        this.sourceNode.connect(this.soundtouchNode);
        this.soundtouchNode.connect(this.audioContext.destination);

        // Set initial pitch
        this.soundtouchNode.parameters.get('pitchSemitones')!.value = this._pitch;

        this.mode = 'soundtouch';
    }

    private _bypassSoundtouch(): void {
        if (!this.audioContext || !this.sourceNode) return;

        // Disconnect soundtouch and route directly to destination
        if (this.soundtouchNode) {
            this.soundtouchNode.disconnect();
        }

        this.sourceNode.disconnect();
        this.sourceNode.connect(this.audioContext.destination);

        this.mode = 'native';
    }

    private _cleanupSoundtouch(): void {
        if (this.sourceNode) {
            this.sourceNode.disconnect();
            this.sourceNode = null;
        }

        if (this.soundtouchNode) {
            this.soundtouchNode.disconnect();
            this.soundtouchNode = null;
        }

        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
    }
}
